import {
  BookButtonClicked,
  BookingConfirmed,
  BusinessEvent,
  BusinessEventHandler, CheckoutPageViewed, ContactHostFormSent, ContactHostFormViewed,
  ExperiencePageViewed,
  PageView, SearchPageViewed
} from "../../businessLayer";

import Cookies from "js-cookie";
import {AB_TEST_COOKIE_NAME} from "~/constants";
import stringToKebabCase from "utils/textHelpers/stringToKebabCase";

import { trackPageView } from '@snowplow/browser-tracker';

function addDefaultParameters(inputParams: object, evt: BusinessEvent) {
  const experimentEventData = {};
  evt.experiments?.forEach((exp) => {
    experimentEventData[`experiment_${exp.id}`] = exp.assignedVariant;
  });
  
  experimentEventData['storefront_ab_test'] = Cookies.get(AB_TEST_COOKIE_NAME) || 'not_in_test';
  
  return {
    traffic_type: evt.traffic_type,
    account_is_host: evt.account_is_host,
    account_id: evt.account_id,
    application: 'ue',
    ...experimentEventData,
    ...inputParams,
  };
}
  
const snowplowHandler: BusinessEventHandler = (evt: BusinessEvent) => {
  if (evt.type === 'PageView') {
    const pageView = evt as PageView;
    const eventData = addDefaultParameters({
      custom_event_type: 'page_view',
      page_title: pageView.title,
      page_location: pageView.location,
      page_path: pageView.path,
    }, pageView);

    console.log('[SNOWPLOW] sending page view:', eventData);
  
    return trackPageView({
      context: [
        {
          schema: 'iglu:com.withlocals.ue/page_view/jsonschema/1-0-0',
          data: eventData,
        },
      ],
    });
  }
  
  if (evt.type === 'SearchPageViewed') {
    const event = evt as SearchPageViewed;
    const eventData = addDefaultParameters({
      custom_event_type: 'page_view_search',
      city: event.city,
      category: event.category,
    }, event);

    console.log('[SNOWPLOW] sending search page view:', eventData);
  
    return trackPageView({
      context: [
        {
          schema: 'iglu:com.withlocals.ue/page_view_search/jsonschema/1-0-0',
          data: eventData,
        },
      ],
    });
  }
  
  if (evt.type === 'ExperiencePageViewed') {
    const event = evt as ExperiencePageViewed;
    const eventData = addDefaultParameters({
      custom_event_type: 'experience_page_viewed',
      experience: event.experience.id,
      area: event.experience.area,
    }, event);

    console.log('[SNOWPLOW] sending experience page view:', eventData);
  
    return trackPageView({
      context: [
        {
          schema: 'iglu:com.withlocals.ue/experience_page_viewed/jsonschema/1-0-0',
          data: eventData,
        },
      ],
    });
  }

  if (evt.type === 'BookButtonClicked') {
    const event = evt as BookButtonClicked;
    const eventData = addDefaultParameters({
      experience_id: event?.experience?.id,
    }, event);

    return trackPageView({
      context: [
        {
          schema: 'iglu:com.withlocals.ue/book_button_clicked/jsonschema/1-0-0',
          data: eventData,
        },
      ],
    });
  }
  
  if (evt.type === 'BookingConfirmed') {
    const event = evt as BookingConfirmed;
    const eventData = addDefaultParameters({
      custom_event_type: 'checkout_completed',
      trip_id: event.trip.id,
      amount: event.trip.required_payment,
      payment_id: event?.trip?.payment?.id,
      city: event?.trip?.city,
    }, event);
  
    console.log('[SNOWPLOW] sending booking confirmed:', eventData);

    return trackPageView({
      context: [
        {
          schema: 'iglu:com.withlocals.ue/checkout_completed/jsonschema/1-0-0',
          data: eventData,
        },
      ],
    });
  }
  
  if (evt.type === 'CheckoutPageViewed') {
    const event = evt as CheckoutPageViewed;
    const eventData = addDefaultParameters({
      custom_event_type: 'page_view_checkout',
      experience: event?.trip?.experience_id,
    }, event);

    console.log('[SNOWPLOW] sending checkout page view:', eventData);
  
    return trackPageView({
      context: [
        {
          schema: 'iglu:com.withlocals.ue/page_view_checkout/jsonschema/1-0-0',
          data: eventData,
        },
      ],
    });
  }
  
  if (evt.type === 'ContactHostFormViewed') {
    const event = evt as ContactHostFormViewed;
    const hostLocation = event?.host?.hostLocations?.[0];
    const eventData = addDefaultParameters({
      custom_event_type: 'page_view_contact_host_form',
      experience_id: event?.experience?.id,
      host_id: event?.host?.id,
      source: event?.source,
      enabledForm: event?.enabledForm,
      country: stringToKebabCase(hostLocation?.country),
      city: stringToKebabCase(hostLocation?.area),
    }, event);
  
    console.log('[SNOWPLOW] sending contact host form viewed:', eventData);

    return trackPageView({
      context: [
        {
          schema: 'iglu:com.withlocals.ue/page_view_contact_host_form/jsonschema/1-0-0',
          data: eventData,
        },
      ],
    });
  }
  
  if (evt.type === 'ContactHostFormSent') {
    const event = evt as ContactHostFormSent;
    const hostLocation = event?.host?.hostLocations?.[0];
    const eventData = addDefaultParameters({
      custom_event_type: 'contact_host_form_sent',
      experience_id: event?.experience?.id,
      host_id: event?.host?.id,
      source: event?.source,
      enabledForm: event?.enabledForm,
      country: stringToKebabCase(hostLocation?.country),
      city: stringToKebabCase(hostLocation?.area),
      nrOfAdults: event?.nrOfAdults,
      nrOfChildren: event?.nrOfChildren,
      date: event?.date,
      time: event?.time,
    }, event);
  
    console.log('[SNOWPLOW] sending contact host form sent:', eventData);

    return trackPageView({
      context: [
        {
          schema: 'iglu:com.withlocals.ue/contact_host_form_sent/jsonschema/1-0-0',
          data: eventData,
        },
      ],
    });
  }
  
  console.warn('[SNOWPLOW] received non-implemented event:', evt);
};

export {
  snowplowHandler
}
